import { useEffect, useMemo, useState } from 'react'
import { Plus, RefreshCw, SquarePen } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ProfileFieldsModal } from './ProfileFieldsModal'
import { FieldValueInput } from './FieldValueInput'
import { ReorderCalculatorPanel } from './ReorderCalculatorPanel'
import { useConditionOptionLabel } from './storage/ConditionControls'
import { formatFieldValue, resolveProfileFields } from '@/lib/profileFields'
import { coerceFieldValue, describeFieldRules, validateFieldValue } from '@/lib/fieldValidation'
import { useFieldDefs, useProfiles, useSaveMaterial } from '@/hooks/useInventoryMaster'
import type { UpdateMaterialPayload } from '@/api/inventoryApi'
import {
  PROFILE_TYPE_COLOR,
  PROFILE_TYPE_LABEL,
  materialProfileRef,
  profileFkField,
} from '@/lib/inventoryProfiles'
import type { ApiFieldDef, ApiMaterial, ApiProfile, InventoryProfileType } from '@/types/inventory'

type Mode = 'view' | 'profile' | 'values'

interface ProfilePopupProps {
  material: ApiMaterial | null
  profileType: InventoryProfileType | null
  open: boolean
  onClose: () => void
}

function pill(active: boolean): string {
  return `flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
    active
      ? 'border-primary bg-primary text-primary-foreground'
      : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
  }`
}

/**
 * A material's profile-column modal. Opens read-only (shows the assigned profile's
 * enabled fields with the material's existing values). Two toggles switch modes:
 * "Change profile" (pick/assign a named profile) and "Edit values" (enter per-material
 * values). Config-only categories (actions/status) have no assign.
 */
export function ProfilePopup({ material, profileType, open, onClose }: ProfilePopupProps) {
  const { data: profiles = [] } = useProfiles(profileType ?? undefined)
  const { data: defs = [] } = useFieldDefs(profileType ?? undefined)
  const saveMaterial = useSaveMaterial()
  // Storage conditions are stored as enum values so put-away can compare them;
  // this reads them back as words. A pass-through for every other enum field.
  const conditionLabel = useConditionOptionLabel()

  const assignedId =
    material && profileType ? (materialProfileRef(material, profileType)?.id ?? undefined) : undefined
  const [assignedOverride, setAssignedOverride] = useState<string | null>(null)
  const effectiveAssignedId = assignedOverride ?? assignedId
  const [selectedId, setSelectedId] = useState('')
  const activeId = selectedId || effectiveAssignedId || profiles[0]?.id || ''
  const profile = profiles.find((p) => p.id === activeId)

  // Opens ready to type. Entering values is what this popup is nearly always
  // for, and requiring a toggle first meant every edit cost an extra click —
  // most of all on a new material, where every field starts empty. "Edit
  // values" still toggles back off for read-only viewing.
  const [mode, setMode] = useState<Mode>('values')
  const [values, setValues] = useState<Record<string, unknown>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [saved, setSaved] = useState(false)
  const [editing, setEditing] = useState<{ profile: ApiProfile | null } | null>(null)

  const assignable = !!profileType && profileFkField(profileType) !== ''
  const enabled = profile ? resolveProfileFields(defs, profile.fields).filter((f) => f.active) : []
  // The resolved rows carry only display data; the full def holds `options` and
  // `validation`, which drive both the input widget and the inline checking.
  const defByKey = useMemo(() => new Map(defs.map((d) => [d.key, d])), [defs])
  const materialAttrs = useMemo(
    () => (material?.attributes as Record<string, { value?: unknown }> | undefined) ?? {},
    [material],
  )
  const valueFor = (key: string): unknown => (key in values ? values[key] : materialAttrs[key]?.value)
  const valuesDirty = Object.keys(values).length > 0

  useEffect(() => {
    setSelectedId('')
    setAssignedOverride(null)
    setMode('values')
    setValues({})
    setErrors({})
    setSaved(false)
  }, [material?.id, profileType, open])
  useEffect(() => {
    setValues({})
    setErrors({})
  }, [activeId])

  /** Store an edited value and re-check just that field, so errors clear as they're fixed. */
  function setValue(def: ApiFieldDef, raw: unknown) {
    setValues((p) => ({ ...p, [def.key]: raw }))
    const message = validateFieldValue(def, coerceFieldValue(def.dataType, raw))
    setErrors((p) => {
      const next = { ...p }
      if (message) next[def.key] = message
      else delete next[def.key]
      return next
    })
  }

  function toggleMode(m: Mode) {
    setMode((cur) => (cur === m ? 'view' : m))
  }
  function flash() {
    setSaved(true)
    window.setTimeout(() => setSaved(false), 2500)
  }

  function assign(id: string) {
    if (!material || !profileType) return
    const payload = { [profileFkField(profileType)]: id } as UpdateMaterialPayload
    saveMaterial.mutate(
      { id: material.id, payload },
      { onSuccess: () => { setAssignedOverride(id); flash(); setMode('view') } },
    )
  }
  function saveValues() {
    if (!material) return

    // Coerce first, then validate — an <input type='number'> hands back a STRING,
    // and the backend requires a real number, so posting the raw value made every
    // number save fail with "Field X must be a number".
    const attributes: Record<string, { active: boolean; value: unknown }> = {}
    const found: Record<string, string> = {}
    for (const f of enabled) {
      const def = defByKey.get(f.key)
      const value = def ? coerceFieldValue(def.dataType, valueFor(f.key)) : valueFor(f.key)
      const message = def ? validateFieldValue(def, value) : null
      if (message) found[f.key] = message
      attributes[f.key] = { active: true, value }
    }
    if (Object.keys(found).length > 0) {
      setErrors(found) // surface inline rather than round-tripping for a 400
      return
    }
    setErrors({})

    saveMaterial.mutate(
      { id: material.id, payload: { attributes } },
      // Stay in edit mode after saving — the "Saved" flash is the confirmation,
      // and dropping to read-only would make the next edit cost another toggle.
      // Local `values` are kept so the fields still show what was just written;
      // the `material` prop is a stale list snapshot that doesn't refresh while
      // the popup stays open.
      { onSuccess: () => flash() },
    )
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className='sm:max-w-lg'>
          <DialogHeader className='space-y-1'>
            {/* The title carries the category's colour, so the popup is
                recognisably the same category the row was opened from. */}
            <DialogTitle
              className={`text-lg ${profileType ? PROFILE_TYPE_COLOR[profileType].ink : ''}`}
            >
              {profileType ? PROFILE_TYPE_LABEL[profileType] : ''}
            </DialogTitle>
            {material && (
              <p className='text-sm text-muted-foreground'>
                <span className='font-mono'>{material.materialId}</span> · {material.materialName}
              </p>
            )}
          </DialogHeader>

          <div className='flex flex-wrap items-center justify-between gap-3'>
            <div className='flex items-center gap-2 text-sm'>
              <span className='text-gray-500'>Profile</span>
              <span className='font-semibold text-gray-800'>{profile?.name ?? '—'}</span>
              {profile && (
                <span className='rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-600'>
                  {enabled.length} YES
                </span>
              )}
            </div>
            {material && (
              <div className='flex items-center gap-1.5'>
                {assignable && (
                  <button type='button' className={pill(mode === 'profile')} onClick={() => toggleMode('profile')}>
                    <RefreshCw className='h-3.5 w-3.5' />
                    Change profile
                  </button>
                )}
                <button type='button' className={pill(mode === 'values')} onClick={() => toggleMode('values')}>
                  <SquarePen className='h-3.5 w-3.5' />
                  Edit values
                </button>
              </div>
            )}
          </div>

          {saved && (
            <span className='text-sm font-medium text-green-600'>✓ Saved for this material</span>
          )}

          {mode === 'profile' && (
            <div className='space-y-2 rounded-lg bg-gray-50 p-3'>
              <div className='flex items-center gap-2'>
                <Select value={activeId} onValueChange={setSelectedId}>
                  <SelectTrigger className='h-9 w-56'>
                    <SelectValue placeholder='Select a profile' />
                  </SelectTrigger>
                  <SelectContent>
                    {profiles.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                        {p.id === effectiveAssignedId ? ' ✓' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  type='button'
                  className='flex items-center gap-1 rounded-full border border-dashed border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-500 hover:border-primary hover:text-primary'
                  onClick={() => setEditing({ profile: null })}
                >
                  <Plus className='h-3.5 w-3.5' />
                  New
                </button>
              </div>
              {activeId && activeId !== effectiveAssignedId && (
                <Button size='sm' onClick={() => assign(activeId)} disabled={saveMaterial.isPending}>
                  {saveMaterial.isPending ? 'Saving…' : `Use “${profile?.name}” for this material`}
                </Button>
              )}
              {profile && (
                <button
                  type='button'
                  className='block text-xs font-medium text-primary hover:underline'
                  onClick={() => setEditing({ profile })}
                >
                  Configure this profile’s fields…
                </button>
              )}
            </div>
          )}

          {profile && (
            <div className='rounded-xl border border-gray-200'>
              <ul className='max-h-[44vh] overflow-y-auto'>
                {enabled.map((f) => {
                  const v = valueFor(f.key)
                  const def = defByKey.get(f.key)
                  const error = errors[f.key]
                  return (
                    <li key={f.key} className='border-b border-gray-50 px-4 py-2 last:border-0'>
                      <div className='flex items-center justify-between gap-3'>
                        <span className='min-w-0 text-sm text-gray-600'>
                          {f.label}
                          {def?.validation?.required && <span className='ml-0.5 text-red-500'>*</span>}
                          {/* Tell the user the constraint up front, rather than
                              only as an error after a rejected save. */}
                          {mode === 'values' && def && describeFieldRules(def) && (
                            <span className='block text-[11px] font-normal text-muted-foreground'>
                              {describeFieldRules(def)}
                            </span>
                          )}
                        </span>
                        {mode === 'values' && def ? (
                          <FieldValueInput
                            def={def}
                            value={v}
                            invalid={!!error}
                            optionLabel={conditionLabel}
                            onChange={(nv) => setValue(def, nv)}
                            onLocalError={(msg) =>
                              setErrors((p) => {
                                const next = { ...p }
                                if (msg) next[def.key] = msg
                                else delete next[def.key]
                                return next
                              })
                            }
                          />
                        ) : (
                          <span className='text-sm font-medium text-gray-900'>
                            {f.dataType === 'enum' && v != null && v !== ''
                              ? conditionLabel(String(v))
                              : formatFieldValue(f.dataType, v)}
                          </span>
                        )}
                      </div>
                      {mode === 'values' && error && (
                        <p className='mt-1 text-right text-xs text-destructive'>{error}</p>
                      )}
                    </li>
                  )
                })}
                {enabled.length === 0 && (
                  <li className='px-4 py-6 text-center text-sm text-muted-foreground'>No fields enabled.</li>
                )}
              </ul>
            </div>
          )}

          {/* The re-order figures, recomputed as the fields above are edited
              (z8nrz7f7dz). Results only: the inputs are those fields. */}
          {profileType === 'inventory_unit' && material && (
            <ReorderCalculatorPanel
              get={valueFor}
              stock={{
                onHand: material.totalQuantity ?? 0,
                available: material.availableQuantity ?? material.totalQuantity ?? 0,
                onOrder: material.reorder?.onOrder ?? 0,
              }}
            />
          )}

          <div className='flex justify-end gap-2'>
            {mode === 'values' && valuesDirty && (
              <Button onClick={saveValues} disabled={saveMaterial.isPending}>
                {saveMaterial.isPending ? 'Saving…' : 'Save values'}
              </Button>
            )}
            <Button variant='outline' onClick={onClose}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {profileType && (
        <ProfileFieldsModal
          profileType={profileType}
          profile={editing?.profile ?? null}
          open={editing !== null}
          onClose={() => setEditing(null)}
          onSaved={(id) => setSelectedId(id)}
        />
      )}
    </>
  )
}
